import { FieldType, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import validator from "validator";
import { UserFile } from "Globals/Models/UserFile";

// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";

// Models
import { CurrentPropertyDetailsModel } from "Globals/Models/Domain/FormSections/CurrentPropertyDetails/CurrentPropertyDetailsModel";
import { CurrentPropertyDetailDTO } from "Globals/Models/DTO/CurrentPropertyDetailDTO";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { HomeOwnerStatusEnum } from "Globals/Models/HomeOwnerStatusEnum";

export default class CurrentPropertyDetailsViewModel extends FormSectionViewModelBase<CurrentPropertyDetailsModel> {
    constructor(currentPropertyDetailsModel: CurrentPropertyDetailsModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, currentPropertyDetailsModel);
        this.saveStore.addCallback(FormSectionsEnum.CurrentPropertyDetails, this.save);
        this.saveFunction = this.save;
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.CurrentPropertyDetails, this.canProceed);
        }
    }

    @computed
    public get getUserFiles(): UserFile[] {
        let userFiles: UserFile[] = [];

        let name = this.getValue<string>("arrearsFileName");
        let url = this.getValue<string>("arrearsDocumentUrl");

        if (!name || !url) {
            return userFiles;
        }

        let userFile: UserFile = { id: this.getValue("id"), fileName: name, fileUrl: url, documentTypeId: null };

        userFiles.push(userFile);

        return userFiles;
    }

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isMyModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;
            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertCurrentPropertyDetails: CurrentPropertyDetailDTO = this.model.toDto();
                const apiResult = await this.Post<CurrentPropertyDetailDTO>(AppUrls.Server.ApplicationForm.Upsert.CurrentPropertyDetails, upsertCurrentPropertyDetails);

                if (apiResult.wasSuccessful) {
                    this.model.fromCurrentPropertyDetailDto(apiResult.payload);
                    console.info("CurrentPropertyDetails:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.CurrentPropertyDetails, this.canProceed);

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("CurrentPropertyDetails:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };
    /**
     * Custom model validation function. Validates child view models.
     * @returns boolean If model is valid or not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) == false) {
            isValid = false;
        }
        return isValid;
    };

    /**
     * Clears conditional fields from the model.
     */
    private clearConditionalModelData(): void {
        if (this.model.hasNegativeEquity == false) {
            this.model.negativeEquityNotes = "";
        }
        if (this.model.isSellingWithEstateAgent == false) {
            this.model.estateAgentName = "";
            this.model.estateAgentPhone = "";
        }
        if (this.model.isSharedOwnership == false) {
            this.model.percentageOwned = null;
            this.model.housingAssociationName = "";
            this.model.housingAssociationPhone = "";
            this.model.housingAssociationAddressLine1 = "";
            this.model.housingAssociationAddressLine2 = "";
            this.model.housingAssociationAddressLine3 = "";
            this.model.housingAssociationCity = "";
            this.model.housingAssociationCounty = "";
            this.model.housingAssociationPostcode = "";
            this.model.movingReason = "";
        }
        //Clear data based on home ownership status
        if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.Selling) {
            this.model.saleCompletedDate = null;
            this.model.equityReceived = null;
            this.model.arrearsDocumentUrl = "";
            this.model.arrearsFileName = "";
            this.model.transferDate = null;
            this.model.saleDocumentUrl = "";
            this.model.saleFileName = "";
        }
        if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.Sold) {
            this.model.mortgageAmountOutstanding = null;
            this.model.negativeEquityNotes = "";
            this.model.hasBuyerForProperty = false;
            this.model.isSellingWithEstateAgent = false;
            this.model.estateAgentName = "";
            this.model.estateAgentPhone = "";
            this.model.estimatedCompletedDate = null;
            this.model.isSharedOwnership = false;
            this.model.percentageOwned = null;
            this.model.housingAssociationName = "";
            this.model.housingAssociationPhone = "";
            this.model.housingAssociationAddressLine1 = "";
            this.model.housingAssociationAddressLine2 = "";
            this.model.housingAssociationAddressLine3 = "";
            this.model.housingAssociationCity = "";
            this.model.housingAssociationCounty = "";
            this.model.housingAssociationPostcode = "";
            this.model.movingReason = "";
            this.model.transferDate = null;
            this.model.transferAmountReceived = null;
            this.model.saleDocumentUrl = "";
            this.model.saleFileName = "";
        }
        if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.RemovingNameFromMortgage) {
            this.model.mortgageAmountOutstanding = null;
            this.model.hasNegativeEquity = false;
            this.model.negativeEquityNotes = "";
            this.model.hasBuyerForProperty = false;
            this.model.isSellingWithEstateAgent = false;
            this.model.estateAgentName = "";
            this.model.estateAgentPhone = "";
            this.model.estimatedCompletedDate = null;
            this.model.isSharedOwnership = false;
            this.model.percentageOwned = null;
            this.model.housingAssociationName = "";
            this.model.housingAssociationPhone = "";
            this.model.housingAssociationAddressLine1 = "";
            this.model.housingAssociationAddressLine2 = "";
            this.model.housingAssociationAddressLine3 = "";
            this.model.housingAssociationCity = "";
            this.model.housingAssociationCounty = "";
            this.model.housingAssociationPostcode = "";
            this.model.movingReason = "";
            this.model.saleCompletedDate = null;
            this.model.arrearsDocumentUrl = "";
            this.model.arrearsFileName = "";
            this.model.transferAmountReceived = null;
            this.model.saleDocumentUrl = "";
            this.model.saleFileName = "";
        }
        if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.RemovedNameFromMortgage) {
            this.model.mortgageAmountOutstanding = null;
            this.model.hasNegativeEquity = false;
            this.model.negativeEquityNotes = "";
            this.model.hasBuyerForProperty = false;
            this.model.isSellingWithEstateAgent = false;
            this.model.estateAgentName = "";
            this.model.estateAgentPhone = "";
            this.model.estimatedCompletedDate = null;
            this.model.isSharedOwnership = false;
            this.model.percentageOwned = null;
            this.model.housingAssociationName = "";
            this.model.housingAssociationPhone = "";
            this.model.housingAssociationAddressLine1 = "";
            this.model.housingAssociationAddressLine2 = "";
            this.model.housingAssociationAddressLine3 = "";
            this.model.housingAssociationCity = "";
            this.model.housingAssociationCounty = "";
            this.model.housingAssociationPostcode = "";
            this.model.movingReason = "";
            this.model.saleCompletedDate = null;
            this.model.equityReceived = null;
            this.model.arrearsDocumentUrl = "";
            this.model.arrearsFileName = "";
        }
    }

    @action
    public handleInputChange = (): void => {
        this.clearConditionalModelData();
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.CurrentPropertyDetails, this.canProceed);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<CurrentPropertyDetailsModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleFieldChange = (fieldName: keyof FieldType<CurrentPropertyDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleRadioChange = (fieldName: keyof FieldType<CurrentPropertyDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<CurrentPropertyDetailsModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.model.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.model.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<CurrentPropertyDetailsModel>;
            let value = this.model.getValue(field);
            if (field == "createdDate") return;
            if (field == "isDeleted") return;
            if (field == "addressLine2") return;
            if (field == "addressLine3") return;
            if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.Selling) {
                if (field == "propertyOwnerName") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "addressLine1") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "city") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "county") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "postcode") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "mortgageAmountOutstanding") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "transferAmountReceived") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "hasNegativeEquity") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (this.model.hasNegativeEquity == true) {
                    if (field == "negativeEquityNotes") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                }
                if (field == "hasBuyerForProperty") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "isSellingWithEstateAgent") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (this.model.isSellingWithEstateAgent == true) {
                    if (field == "estateAgentName") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                    if (field == "estateAgentPhone") {
                        return;
                    }
                }
                if (field == "estimatedCompletedDate") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (this.model.isSharedOwnership == true) {
                    if (field == "percentageOwned") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                    if (field == "housingAssociationName") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                    if (field == "housingAssociationPhone") {
                        return;
                    }
                    if (field == "housingAssociationAddressLine1") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                    if (field == "housingAssociationCity") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                    if (field == "housingAssociationCounty") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                    if (field == "housingAssociationPostcode") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                    if (field == "movingReason") {
                        if (this.isEmptyNullOrUndefined(value) == true) {
                            completed = false;
                        }
                        return;
                    }
                }
            } else if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.Sold) {
                if (field == "propertyOwnerName") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "addressLine1") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "city") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "county") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "postcode") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "saleCompletedDate") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "equityReceived") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "hasNegativeEquity") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "arrearsFileName") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "arrearsDocumentUrl") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
            } else if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.RemovingNameFromMortgage) {
                if (field == "propertyOwnerName") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "addressLine1") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "city") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "county") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "postcode") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "transferDate") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "equityReceived") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
            } else if (StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId) === HomeOwnerStatusEnum.RemovedNameFromMortgage) {
                if (field == "propertyOwnerName") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "addressLine1") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "city") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "county") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "postcode") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "transferDate") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "transferAmountReceived") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "saleFileName") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
                if (field == "saleDocumentUrl") {
                    if (this.isEmptyNullOrUndefined(value) == true) {
                        completed = false;
                    }
                    return;
                }
            }
            if (completed == false) {
                // console.error("Field not completed: ", field, "Value: ", value);
            }
        });
        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<CurrentPropertyDetailsModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<CurrentPropertyDetailsModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        let status = StoresInstance.Domain.HomeOwnerStatusStore.getEnumFromId(this.model.homeOwnerStatusId);
        if (status === HomeOwnerStatusEnum.Selling) {
            if (fieldName == "propertyOwnerName") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "addressLine1") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "city") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "county") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "postcode") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                    if (!validator.isPostalCode(value, "GB")) {
                        errorMessage = "not a valid postcode";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "mortgageAmountOutstanding") {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "transferAmountReceived") {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (this.model.hasNegativeEquity == true) {
                if (fieldName == "negativeEquityNotes") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
            }
            if (fieldName == "hasBuyerForProperty") {
                if (this.isEmptyNullOrUndefined(value) == true) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
            if (fieldName == "isSellingWithEstateAgent") {
                if (this.isEmptyNullOrUndefined(value) == true) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
            if (this.model.isSellingWithEstateAgent == true) {
                console.log(this.model.isSellingWithEstateAgent ? "Is selling with EA." : "Is NOT selling with EA.");
                if (fieldName == "estateAgentName") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
                if (fieldName == "estateAgentPhone") {
                    if (value) {
                        if ((value && !validator.isEmpty(value)) || isRequired) {
                            if (validator.isEmpty(value)) {
                                errorMessage = "required to proceed";
                                isValid = false;
                            }
                        }
                    }
                }
            }
            if (fieldName == "estimatedCompletedDate") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (value == null || validator.isEmpty((value as Date).toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (this.model.isSharedOwnership == true) {
                if (fieldName == "percentageOwned") {
                    if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                        if (value == null || validator.isEmpty(value.toString())) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
                if (fieldName == "housingAssociationName") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
                if (fieldName == "housingAssociationPhone") {
                    if (value) {
                        if ((value && !validator.isEmpty(value)) || isRequired) {
                            if (validator.isEmpty(value)) {
                                errorMessage = "required to proceed";
                                isValid = false;
                            }
                        }
                    }
                }
                if (fieldName == "housingAssociationAddressLine1") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
                if (fieldName == "housingAssociationCity") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
                if (fieldName == "housingAssociationCounty") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
                if (fieldName == "housingAssociationPostcode") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                        if (!validator.isPostalCode(value, "GB")) {
                            errorMessage = "not a valid postcode";
                            isValid = false;
                        }
                    }
                }
                if (fieldName == "movingReason") {
                    if ((value && !validator.isEmpty(value)) || isRequired) {
                        if (validator.isEmpty(value)) {
                            errorMessage = "required to proceed";
                            isValid = false;
                        }
                    }
                }
            }
        } else if (status === HomeOwnerStatusEnum.Sold) {
            if (fieldName == "propertyOwnerName") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "addressLine1") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "city") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "county") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "postcode") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                    if (!validator.isPostalCode(value, "GB")) {
                        errorMessage = "not a valid postcode";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "saleCompletedDate") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (value == null || validator.isEmpty((value as Date).toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "equityReceived") {
                let amount = value as number;
                if ((amount && !validator.isEmpty(amount.toString())) || isRequired) {
                    if (amount == null || validator.isEmpty(amount.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "arrearsFileName") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "arrearsDocumentUrl") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        } else if (status === HomeOwnerStatusEnum.RemovingNameFromMortgage) {
            if (fieldName == "propertyOwnerName") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "addressLine1") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "city") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "county") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "postcode") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                    if (!validator.isPostalCode(value, "GB")) {
                        errorMessage = "not a valid postcode";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "transferDate") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (value == null || validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "equityReceived") {
                let amount = value as number;
                if ((amount && !validator.isEmpty(amount.toString())) || isRequired) {
                    if (amount == null || validator.isEmpty(amount.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        } else if (status === HomeOwnerStatusEnum.RemovedNameFromMortgage) {
            if (fieldName == "propertyOwnerName") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "addressLine1") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "city") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "county") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "postcode") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                    if (!validator.isPostalCode(value, "GB")) {
                        errorMessage = "not a valid postcode";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "transferDate") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (value == null || validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "transferAmountReceived") {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "saleFileName") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "saleDocumentUrl") {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (validator.isEmpty(value)) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        }
        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<CurrentPropertyDetailsModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
